import { createSelector } from 'reselect'

import { EMPTY_ARRAY } from 'utils/emptyConstants'
import { isMalicious, isSafe } from 'utils/incident'

import {
  selectActualPermissions,
  selectIsActualCompanyDataReady,
  selectIsAllCompaniesSelected,
  selectIsMaster,
} from 'containers/App/selectors'

import { INCIDENT_TYPE } from '../constants'

const selectData = (state) => state.resolutioncenter.report.data
const selectReportLoader = (state) => state.resolutioncenter.report.reportLoader
const selectError = (state) => state.resolutioncenter.report.error

const selectReportersPagination = (state) => state.resolutioncenter.report.reportersPagination
const selectReportersLoading = (state) => state.resolutioncenter.report.reportersLoading

const selectCommunityInsights = (state) => state.resolutioncenter.report.communityInsights

const selectScansFreemiumLimit = (state) => state.resolutioncenter.report.scansFreemiumLimit

const selectViewerPermission = (state) => {
  const perms = state.app.data.permissions
  return (
    perms.irontraps_viewer &&
    !perms.admin &&
    !perms.edit_campaigns &&
    !perms.irontraps_action &&
    !perms.manage_irontraps
  )
}

const selectScannerDetails = (state) => state.resolutioncenter.report.scannerDetails
const selectedAffMailboxesDetails = (state) => state.resolutioncenter.report.affMailboxesDetails

const selectedAffMailboxesCounts = (state) => state.resolutioncenter.report.affMailboxesCounts

const selectAssignees = (state) => state.resolutioncenter.report.assignees
const selectAssigneesLoader = (state) => state.resolutioncenter.report.assignees.loader
const selectAssigneesOptions = createSelector(selectAssignees, (assignees) => {
  const rows = Array.isArray(assignees?.data) ? assignees.data : EMPTY_ARRAY
  return rows.map((assignee) => {
    const assigneeLabel = assignee.first_name
      ? `${assignee.first_name} ${assignee.last_name}`
      : assignee.email
    return { label: assigneeLabel, value: assignee.id }
  })
})

const selectAllowedToClassify = (state) => {
  const perms = selectActualPermissions(state)
  const isAllCompaniesSelected = selectIsAllCompaniesSelected(state)
  const permsBased = perms.admin || perms.irontraps_action || perms.manage_irontraps
  return isAllCompaniesSelected
    ? (state.resolutioncenter.report.data.hasEditPerm ?? permsBased)
    : permsBased
}

// Backend _check_action_permissions accepts only irontraps_action (or master) for MSFT release/close.
// Without the selected company's data (still loading, or the All companies view) the FE cannot
// know the permission, so stay enabled and let the backend decide (its 403 is already expected).
const selectCanActOnMsftQuarantine = (state) =>
  !!selectIsMaster(state) ||
  !selectIsActualCompanyDataReady(state) ||
  !!selectIsAllCompaniesSelected(state) ||
  !!selectActualPermissions(state)?.irontraps_action

const selectDownloadOriginalEmailLoader = (state) =>
  state.resolutioncenter.report.downloadOriginalEmailLoader

const selectShowResolutionTags = (state) => {
  const { data } = state.resolutioncenter.report
  const { enablePhishingTags, enableSafeTags, enableSpamTags } = data
  if (isMalicious(data.state)) return enablePhishingTags
  else if (isSafe(data.state)) return enableSafeTags
  else return enableSpamTags
}

const selectEmailSnapshotUrl = (state) => state.resolutioncenter.report.snapshotUrl
const selectEmailSnapshotLoader = (state) => state.resolutioncenter.report.emailSnapshotLoader

const selectEscalatedEmailCardTabKey = (state) =>
  state.resolutioncenter.report.escalatedEmailCardTabKey

export const selectReportElements = (state) => state.resolutioncenter.report.reportElements

const selectMsftActionLoaderByReportKey = (state) =>
  state.resolutioncenter.report.msftActionLoaderByReportKey

const makeSelectMsftLoaderByKey =
  (reportType = INCIDENT_TYPE.MSFT, reportId) =>
  (state) => {
    const key = `${reportType}-${reportId}`
    return !!selectMsftActionLoaderByReportKey(state)[key]
  }

const THEMIS_ANALYSIS_DEFAULT_STATE = {
  loading: false,
  error: null,
  data: null,
  fieldLoadingStates: {},
  dynamicCards: {},
  dynamicTables: {},
  tasks: null,
  hasReceivedToolCallStart: false,
  isComplete: false,
  activityEvents: [],
  retryCount: 0,
  retryExhausted: false,
}

// react-redux 8's useSyncExternalStore requires selectors to be referentially
// stable for unchanged input; memoize the normalized entry per `entry` object.
const themisAnalysisNormalizedCache = new WeakMap()

const selectThemisAnalysisByReportId = (reportId) => (state) => {
  // Null-safe: the resolutioncenter slice is route-injected, and consumers like
  // PhishingSOCAgent also render in Storybook/mock stores without it.
  const entry = state.resolutioncenter?.report?.themisAnalysis?.byReportId?.[reportId]
  if (!entry) return THEMIS_ANALYSIS_DEFAULT_STATE
  if (Object.hasOwn(entry, 'retryCount') && Object.hasOwn(entry, 'retryExhausted')) {
    return entry
  }
  const cached = themisAnalysisNormalizedCache.get(entry)
  if (cached) return cached
  const normalized = {
    ...entry,
    retryCount: entry.retryCount || 0,
    retryExhausted: entry.retryExhausted || false,
  }
  themisAnalysisNormalizedCache.set(entry, normalized)
  return normalized
}

// Single source of truth for the per-report "out of credits" (402) state so the
// card header/actions and the analysis body can never disagree. Company-level
// creditsRemaining can be stale or unavailable (the credits fetch can fail for
// non-admin users), so the durable 402 flag must be consulted alongside it.
const selectThemisInsufficientCredits = (reportId) => (state) => {
  const entry = selectThemisAnalysisByReportId(reportId)(state)
  return entry.error === 'Insufficient credits' || Boolean(entry.insufficientCredits)
}

// Explicit per-report "user asked to generate" intent (IC-49715). The auto-start
// effects only run when this is set; it is consumed at THEMIS_ANALYSIS_ASYNC.INITIATE.
// Built on selectThemisAnalysisByReportId so it inherits its null-safe default.
const selectThemisGenerateRequested = (reportId) => (state) =>
  Boolean(selectThemisAnalysisByReportId(reportId)(state).generateRequested)

export {
  selectData,
  selectReportersPagination,
  selectReportersLoading,
  selectReportLoader,
  selectError,
  selectViewerPermission,
  selectScannerDetails,
  selectedAffMailboxesDetails,
  selectedAffMailboxesCounts,
  selectAllowedToClassify,
  selectCanActOnMsftQuarantine,
  selectCommunityInsights,
  selectScansFreemiumLimit,
  selectAssigneesOptions,
  selectAssigneesLoader,
  selectDownloadOriginalEmailLoader,
  selectShowResolutionTags,
  selectEmailSnapshotUrl,
  selectEmailSnapshotLoader,
  selectEscalatedEmailCardTabKey,
  selectMsftActionLoaderByReportKey,
  makeSelectMsftLoaderByKey,
  selectThemisAnalysisByReportId,
  selectThemisInsufficientCredits,
  selectThemisGenerateRequested,
}
